import { useEffect, useState } from "react"
import { FieldValues, useForm } from "react-hook-form"
import { useLocation, useNavigate, useParams } from "react-router-dom"
import { toast } from "react-toastify"

import {
  showErrorToast,
  showSuccessToast,
} from "@fuse/sharedComponents/themeToast/toastUtils"
import { yupResolver } from "@hookform/resolvers/yup"
import {
  EVENT_CLASS,
  Question,
  QUESTIONNAIRES_TYPE,
  TAnalyticsEventParams,
} from "@letshang/constants"
import { castArray, get, isEmpty } from "lodash-es"
import { useRecoilState } from "recoil"
import { ROUTES } from "src/App"
import * as yup from "yup"

import { userState } from "@fuse/recoil/signup"

import {
  ELogAnalyticEvent,
  logAnalyticEvent,
} from "@firebaseConfig/analyticsUtil"

import { useJoinEvent } from "@api/services/events/useEventsApi"

import useFetchEventDetails from "@fuse/pages/eventPage/useFetchEventDetails"
import { useBuyTicketsSession } from "@fuse/pages/eventSignUp/BuyTickets/buyTicketsSessionManager"
import { EBuyTicketSessionKeys } from "@fuse/pages/eventSignUp/BuyTickets/utils"

const daysInMonth = {
  1: 31,
  2: 28,
  3: 31,
  4: 30,
  5: 31,
  6: 30,
  7: 31,
  8: 31,
  9: 30,
  10: 31,
  11: 30,
  12: 31,
}

const isObject = (value) => {
  if (typeof value == "object" && !Array.isArray(value)) {
    return true
  }
  return false
}

const isDateObj = (obj) => {
  if (obj["day"] && obj["month"] && obj["year"]) {
    return true
  }
  return false
}

const getISODate = (dateVal) => {
  const { day, month, year } = dateVal
  const date = new Date(year, month, day)
  return date.toISOString()
}

export const useFillQuestionnaire = (invitedId?: string, pagename?: string) => {
  const params = useParams()
  const eventId = get(params, "eventId", "")
  const location = useLocation()
  const navigate = useNavigate()

  const [isLoading, setIsLoading] = useState(false)
  const [user, setUser] = useRecoilState(userState)
  const loggedInUserId = get(user, "uid", "")

  const { state, refresh: refreshEventData } = useFetchEventDetails(eventId)
  const { getBuyTicketsSessionItem } = useBuyTicketsSession()
  const { mutate: mutateJoinEvent, isLoading: joinEventLoading } =
    useJoinEvent()

  const eventLoading = get(state, "isLoading", false)
  const questionnaires = get(location, "state.attendeeQuestioners", [])

  const currentYear = new Date().getFullYear()
  const minYear = currentYear - 80
  const maxYear = currentYear + 80

  const isLeapYear = (year: number) => {
    return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0
  }

  const createValidationSchema = () => {
    const shape: { [key: number]: any } = {}
    if (!isEmpty(questionnaires)) {
      questionnaires.forEach((question: Question, index: number) => {
        switch (question.questionType) {
          case QUESTIONNAIRES_TYPE.EMAIL:
            shape[index] = yup
              .string()
              .email("Please enter a valid email")
              .required("This field is required")
            break
          case QUESTIONNAIRES_TYPE.PHONE:
            shape[index] = yup
              .string()
              .matches(
                /^[0-9]{10}$/,
                "Please enter a valid 10 digit phone number",
              )
              .required("This field is required")
            break
          case QUESTIONNAIRES_TYPE.NUMBER:
            shape[index] = yup
              .number()
              .typeError("Please enter a valid number")
              .required("This field is required")
            break
          case QUESTIONNAIRES_TYPE.DATE:
            shape[index] = yup
              .object()
              .shape({
                day: yup
                  .number()
                  .typeError("Invalid input type for day, must be a number")
                  .required("Day is required")
                  .min(1, "Day must be at least 1")
                  .max(31, "Day must be no more than 31"), // Max for validation, refined below in test()

                month: yup
                  .number()
                  .typeError("Invalid input type for month, must be a number")
                  .required("Month is required")
                  .min(1, "Month must be at least 1")
                  .max(12, "Month must be no more than 12"),

                year: yup
                  .number()
                  .typeError("Invalid input type for year, must be a number")
                  .required("Year is required")
                  .min(
                    minYear,
                    `Year must be greater than or equal to ${minYear}`,
                  )
                  .max(
                    maxYear,
                    `Year must be less than or equal to ${maxYear}`,
                  ),
              })
              .test(
                "valid-day-for-month",
                "Invalid day for the given month and year",
                (values) => {
                  if (!values || !values.day || !values.month || !values.year)
                    return true

                  const { day, month, year } = values

                  // Handle February in leap year
                  if (month === 2) {
                    const maxDays = isLeapYear(year) ? 29 : 28
                    return day <= maxDays
                  }

                  // Check other months based on mapping
                  return day <= daysInMonth[month]
                },
              )
            break
          case QUESTIONNAIRES_TYPE.ADDRESS:
          case QUESTIONNAIRES_TYPE.TEXT:
          case QUESTIONNAIRES_TYPE.LONG_TEXT:
            shape[index] = yup
              .string()
              .typeError("Invalid value")
              .required("This field is required")
            break
          case QUESTIONNAIRES_TYPE.SINGLE_SELECT:
            shape[index] = yup
              .string()
              .typeError("Invalid value")
              .required("This field is required")
            break
          case QUESTIONNAIRES_TYPE.MULTI_SELECT:
            shape[index] = yup
              .array()
              .min(1, "Select at least one option")
              .required("This field is required")
            break
          default:
            break
        }
      })
    }
    return yup.object().shape(shape)
  }

  const validationSchema = createValidationSchema()
  const {
    watch,
    control,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<FieldValues>({
    resolver: yupResolver(validationSchema),
  })

  const currentEventDetails = get(state, "eventDetails", {})
  const flyerImage = get(currentEventDetails, "eventDocData.flyerImage", "")
  const eventClass = get(
    currentEventDetails,
    "eventDocData.eventClass",
    EVENT_CLASS.NON_TICKETED_EVENT,
  ) as EVENT_CLASS
  const isTicketedEvent = eventClass === EVENT_CLASS.TICKETED_EVENT
  const baseQuestionnaires: Question[] = get(
    currentEventDetails,
    "eventDocData.questionnaires",
    [],
  )
  const defaultAnswers = get(location, "state.attendeeQuestionersAnswer", {})
  const answersToQuestions = get(location, "state.answersToQuestions", {})

  const ticketsToBuy = castArray(get(location, "state.ticketsToBuy", []))
  const questionersAttendeeIndex = get(
    location,
    "state.questionersAttendeeIndex",
    "",
  )
  const eventDocData = get(currentEventDetails, "eventDocData", {})
  const isAdmin = get(eventDocData, "initiatorUid", null) === loggedInUserId

  const onLogAnalyticEvent = (): void | undefined => {
    const commonParams: TAnalyticsEventParams = {
      uid: loggedInUserId,
      page_name: pagename,
      event_id: eventId,
      event_name: get(eventDocData, "eventName", ""),
      event_type: isTicketedEvent
        ? EVENT_CLASS.TICKETED_EVENT
        : EVENT_CLASS.NON_TICKETED_EVENT,
    }

    logAnalyticEvent(ELogAnalyticEvent.FILL_QUESTIONNAIRE_WEB, commonParams)
  }

  useEffect(() => {
    if (!isEmpty(eventDocData) && !isAdmin) {
      onLogAnalyticEvent()
    }
  }, [eventDocData])

  const groupTicketsInfoState = getBuyTicketsSessionItem(
    EBuyTicketSessionKeys.GROUP_TICKETS_INFO_STATE,
  )

  const onSubmit = (data: any) => {
    let dataIndex = 0

    baseQuestionnaires.forEach((question, index) => {
      if (index in defaultAnswers) {
        if (isObject(data[index])) {
          if (isDateObj(data[index])) {
            answersToQuestions[index] = getISODate(data[index])
          } else {
            answersToQuestions[index] = "" // if any field is not available then do this
          }
        } else answersToQuestions[index] = defaultAnswers[index] // Insert skipped question answer
      } else if (dataIndex in data) {
        if (isObject(data[dataIndex])) {
          if (isDateObj(data[dataIndex])) {
            answersToQuestions[index] = getISODate(data[dataIndex])
          } else {
            answersToQuestions[index] = "" // if any field is not available then do this
          }
        } else answersToQuestions[index] = data[dataIndex] // Insert non-skipped answer
        dataIndex++ // Move to the next data index
      }
    })

    if (isTicketedEvent) {
      // navigate(ROUTES.STRIPE_CHECKOUT.replace(":eventId", eventId), {
      //   state: {
      //     ticketsToBuy: ticketsToBuy,
      //     answersToQuestions,
      //     invitedId: invitedId,
      //   },
      // })
      navigate(ROUTES.FILL_FRIENDS_TICKET_INFO.replace(":eventId", eventId), {
        state: {
          ticketsToBuy: ticketsToBuy,
          answersToQuestions,
          questionersAttendeeIndex,
          invitedId: invitedId,
          groupTicketsInfo: groupTicketsInfoState,
        },
      })
    } else {
      const payload = {
        eventId: eventId,
        answersToQuestions,
      }
      mutateJoinEvent(payload, {
        onSuccess: (data) => {
          const isSuccess = get(data, "success", false)
          if (isSuccess) {
            refreshEventData()
            showSuccessToast(
              `You successfully joined "${state?.eventDetails?.eventDocData?.eventName}"`,
            )
            navigate(ROUTES.EVENTS.replace(":eventId", eventId))
          } else {
            const message = get(data, "message", "Something went wrong!")
            showErrorToast(`Error: ${message}`)
          }
        },
        onError: (err: any) => {
          showErrorToast(`Error: ${err.message}`)
        },
      })
    }
  }

  return {
    eventId,
    isLoading: isLoading || eventLoading || joinEventLoading,
    questionnaires,
    watch,
    handleSubmit,
    errors,
    onSubmit,
    flyerImage,
    control,
  }
}
